import { Subscription } from 'rxjs';
import { ICheckerView, GameState } from './interfaces';
import { LineColors, LineStates, LineOrientations, DoubleCubeData, BackgammonPhases } from './types';
import CheckerStack from './checkerStack';
import CheckerView from './checkerView';
import Color from '@base/features/game/model/color';
import DoubleCubeView from './doubleCubeView';
import GameLine from './gameLine';
import { getPositionsForLines } from './linePositions';
import Move from './move';
import { Coordinates, TableSides } from '@base/features/game/model/types';
import DiceView from './diceView';
import { DiceObservable } from './dice';
import { IPreferencesService } from '@base/features/preferences/model/types';
import GameStateImpl from './gameState';
import { Preferences } from '@backgammon/constants/defaultPreferences';
import ImageProvider from '@base/features/image/imageProvider';
import { BackgammonImageCollectionKeys } from '@backgammon/constants/imageCollection';
import Sizes from './sizes';
import Drawable from '@base/features/game/model/drawable';

export default class GameBoard {
  lines: GameLine[] = [];
  linesCount = 28;
  canvas: HTMLCanvasElement;
  canvasDirection = 1;
  context: CanvasRenderingContext2D;
  doubleCube: DoubleCubeView;
  boardDrawable: Drawable;
  diceView: DiceView;
  backgroundDrawable: Drawable;
  checkerInAir?: ICheckerView;
  sourceLine?: GameLine;
  insertLine?: GameLine;
  animatedCheckers: ICheckerView[] = [];
  ANIMATION_SPEED = 0.9;
  CLICK_ANIMATION_SPEED = 1.7;
  BACK_ANIMATION_SPEED = 1.7;
  availableLineNumbers: (number | undefined)[] | null = [];
  movesQueue: Move[] = [];
  doubleClickDelayMs = 200; // 200ms
  currentTouchId = -1;
  destroying = false;
  lastFrameTime = 0;
  lastClickTime = 0;
  busyAnimationTime = 200; //200ms
  firstLayoutMade = false;
  delayedRequest?: NodeJS.Timeout;
  throwFromBottom = true;
  singleClickPref?: boolean;
  myHomeIsOnLeftPref?: boolean;
  animationInProgress = false;
  needsLayout = false;
  needsRedraw = false;
  lastRender?: number = undefined;
  mouseDownPos: { x: number; y: number } = {
    x: 0,
    y: 0,
  };
  subscriptions: Subscription[] = [];
  now = 0;
  then = Date.now();
  elapsed = 0;
  fps = 30;
  fpsInterval = 1000 / this.fps;
  isDoubleCube = false;

  constructor(
    private _gameState: GameState,
    private imageProvider: ImageProvider<BackgammonImageCollectionKeys>,
    private preferencesService: IPreferencesService<Preferences>,
    private gameSizes: Sizes
  ) {
    this.canvas = document.getElementById('cnvmain') as HTMLCanvasElement;
    if (!this.canvas) throw new Error('Error finding canvas in document.');
    this.context = this.canvas.getContext('2d') as CanvasRenderingContext2D;
    if (!this.context) throw new Error('Error initializing canvas element.');

    this.createLines(this.context);
    this.doubleCube = new DoubleCubeView(imageProvider, this.context);
    this.isDoubleCube = this._gameState.getDoubleCube().isDoubleAllowed();
    this.boardDrawable = new Drawable(this.context);
    this.backgroundDrawable = new Drawable(this.context);
    this.boardDrawable.image = imageProvider.get('Board');
    // this.backgroundDrawable.image = imageProvider.Background.image;
    this.diceView = new DiceView(imageProvider.get('Dice1'), imageProvider.get('Dice2'));

    const customReqAnimFrame = (callback: Function) => {
      window.setTimeout(callback, 1000 / 60);
    };

    (window as any).requestAnimFrame =
      window.requestAnimationFrame || window.webkitRequestAnimationFrame || customReqAnimFrame;

    const animEventHandler = () => this.animLoop();
    this.inactiveLoop();
    (window as any).requestAnimFrame(animEventHandler);
    this.singleClickPref = this.preferencesService.getPreferences('DOUBLE_CLICK_MOVE') as boolean;
    this.myHomeIsOnLeftPref = this.preferencesService.getPreferences('MY_HOME_IS_ON_LEFT') as boolean;
    this.addListeners();
    this._gameState.forceRenew();
  }

  addListeners = () => {
    this.removeListeners();
    this.canvas.addEventListener('mousedown', this.mouseDown);
    this.canvas.addEventListener('mousemove', this.mouseMove);
    this.canvas.addEventListener('mouseup', this.mouseUp);
    this.canvas.addEventListener('touchstart', this.touchDown);
    this.canvas.addEventListener('touchmove', this.touchMove);
    this.canvas.addEventListener('touchend', this.touchEnd);
    window.addEventListener('touchend', this.touchEnd);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('focus', this.onResize);
    window.addEventListener('touchcancel', this.touchCancel);
    this.canvas.addEventListener('touchcancel', this.touchCancel);

    this.subscriptions = [
      this._gameState.onCheckerMoved.subscribe(this.onMoveFromModelReceived),
      this._gameState.onInvalidateEvent.subscribe(this.onInvalidate),
      this._gameState.getDoubleCube().onDoubleCubeChangedEvent.subscribe(this.onDoubleCubeChanged),
      this._gameState.getDice().onDiceChangedEvent.subscribe(this.onDiceChanged),
      this.preferencesService.onPreferencesChanged.subscribe((preferences) => {
        this.singleClickPref = preferences.DOUBLE_CLICK_MOVE;
        this.myHomeIsOnLeftPref = preferences.MY_HOME_IS_ON_LEFT;
      }),
    ];
    this.animateNextMove();
  };

  removeListeners = () => {
    this.canvas.removeEventListener('mousedown', this.mouseDown);
    this.canvas.removeEventListener('mousemove', this.mouseMove);
    this.canvas.removeEventListener('mouseup', this.mouseUp);
    this.canvas.removeEventListener('touchstart', this.touchDown);
    this.canvas.removeEventListener('touchmove', this.touchMove);
    this.canvas.removeEventListener('touchend', this.touchEnd);
    window.removeEventListener('touchend', this.touchEnd);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('focus', this.onResize);

    this.canvas.removeEventListener('touchcancel', this.touchCancel);
    window.removeEventListener('touchcancel', this.touchCancel);

    this.subscriptions.forEach((s) => s.unsubscribe());
  };

  /**
   * метод вызываемый событием хода из модели
   * @param {Move} move - ход
   */
  onMoveFromModelReceived = (move: Move | Move[]) => {
    if (Array.isArray(move)) return;
    console.debug('move from model recieved: ' + move.sourceLineNumber + ' -> ' + move.insertLineNumber);
    move.started = false;
    this.movesQueue.push(move);
  };

  onDiceChanged = (dice: DiceObservable) => {
    if (this.gameState.imPlaying()) {
      this.throwFromBottom = this.gameState.isMyTurn() || !this.gameState.isGameInProgress();
    } else {
      this.throwFromBottom = this.gameState.currentTurn === TableSides.SOUTH;
    }
    // this.throwFromBottom = this.gameState.imPlaying()
    //   ? this.gameState.isMyTurn()
    //   : this.gameState.currentTurn === TableSides.SOUTH;

    dice.withRoll ? this.diceView.roll([dice.firstValue, dice.secondValue]) : this.diceView.move(dice);
  };

  animateMove(move: Move) {
    move.started = true;
    for (let i = 0; i < move.getAdditionalMoves().length; i++) {
      this.moveCheckerWithAnimation(move.getAdditionalMoves()[i]);
    }
    this.moveCheckerWithAnimation(move);
  }

  animateNextMove = () => {
    if (this.destroying) {
      return;
    }
    if (this.animatedCheckers.length || !this.firstLayoutMade) {
      setTimeout(() => this.animateNextMove(), 200);
      return;
    }

    if (this.movesQueue.length) {
      for (let i = 0; i < this.movesQueue.length; i++) {
        if (!this.movesQueue[i].started) {
          this.animateMove(this.movesQueue[i]);
          break;
        }
      }
    }
    setTimeout(() => this.animateNextMove(), 200);
    return;
  };

  moveCheckerWithAnimation(move: Move) {
    this.animationInProgress = true;
    let speed = this.ANIMATION_SPEED;
    const sourceLine = move.sourceLineNumber != null ? this.getLine(move.sourceLineNumber) : null;
    const insertLine = this.getLine(move.insertLineNumber);
    if (!insertLine) return;
    let moveChecker: ICheckerView | undefined = undefined;

    if (sourceLine === null) {
      if (!this.checkerInAir) {
        // TODO Console
        // window.Console.sendLogToServer(new Error('Checker in air is null'));
      }

      if (this.checkerInAir?.getColor() === move.color) {
        moveChecker = this.checkerInAir;
        this.checkerInAir = undefined;
      }
    } else {
      const topChecker = sourceLine.getTopChecker();
      if (topChecker) moveChecker = topChecker;
    }

    if (!moveChecker) {
      // TODO Console
      // window.Console.sendLogToServer(new Error('Move Checker is null'));
      throw new Error('Move Checker is null');
    }

    const cordsTo = insertLine.getPointForNewChecker();
    const fromCords = {
      x: moveChecker.position.x || cordsTo?.x,
      y: moveChecker.position.y || cordsTo?.y,
    };
    const currentTime = new Date().getTime();
    moveChecker.setAnimating(false);
    this.animatedCheckers.push(moveChecker);

    if (move.getByClick()) {
      speed = this.CLICK_ANIMATION_SPEED;
    }

    this.requestRedraw();
    this.requestLayout();

    moveChecker.createAnimation(
      fromCords,
      cordsTo,
      speed,
      currentTime,
      insertLine,
      (checkerView: ICheckerView, toLine: GameLine) => {
        checkerView.setAnimating(false);
        toLine.addChecker(checkerView);
        this.removeCheckerFromAnimated(checkerView);
        checkerView.animation = undefined;
        this.checkerInAir = undefined;
        this.requestRedraw();
        this.requestLayout();
        this.checkBoard();
      }
    );
    moveChecker.setAnimating(true);
  }

  requestRedraw = () => {
    this.needsRedraw = true;
  };

  requestLinesLayout = () => {
    for (const line of this.lines) {
      line.needLayout = true;
    }
  };

  onDoubleCubeChanged = (cube: DoubleCubeData) => {
    this.doubleCube.setValue(cube.value);
    this.doubleCube.setColor(cube.color);
    if (cube.active) {
      this.doubleCube.setActive();
    }
    this.requestRedraw();
  };

  onInvalidate = () => {
    const stacks = this._gameState.getStacks();
    this.invalidate(stacks);
    this.requestLayout();
    this.requestLinesLayout();
  };

  invalidate = (stacks: CheckerStack[]) => {
    for (let i = 0; i < this.lines.length; i++) {
      this.lines[i].clear();
    }
    for (let i = 0; i < stacks.length; i++) {
      const currentLine = this.getLine(stacks[i].getNumber());
      for (let j = 0; j < stacks[i].getCheckersCount(); j++) {
        let newCheckerView;
        const checkersColor = stacks[i].getCheckersColor();
        if (checkersColor && currentLine) {
          newCheckerView = new CheckerView(checkersColor, this.imageProvider, this.context);
          currentLine.addChecker(newCheckerView);
        }
      }
    }
    this.requestRedraw();
  };

  getLine = (num: number) => {
    for (let i = 0; i < this.lines.length; i++) {
      if (this.lines[i].getNumber() === num) {
        return this.lines[i];
      }
    }
    return null;
  };

  onResize = () => {
    if (this.delayedRequest) {
      clearTimeout(this.delayedRequest);
      this.delayedRequest = undefined;
    }

    this.delayedRequest = setTimeout(() => {
      this.requestLayout();
      this.requestLinesLayout();
      this.delayedRequest = undefined;
    }, 250);
  };

  requestLayout = () => {
    this.needsLayout = true;
    this.needsRedraw = true;
  };

  animLoop = () => {
    if (this.destroying) return;
    this.now = Date.now();
    this.elapsed = this.now - this.then;
    if (this.elapsed > this.fpsInterval) {
      this.then = this.now - (this.elapsed % this.fpsInterval);
      const animationRunning = this.isAnimating();
      this.needsRedraw = this.needsRedraw || animationRunning;
      this.lastFrameTime = new Date().getTime();
      if (this.needsLayout) {
        this.layout();
        this.needsLayout = false;
        this.needsRedraw = true;
      }
      if (this.needsRedraw) {
        this.render();
        this.needsRedraw = false;
      }
    }
    (window as any).requestAnimFrame(this.animLoop);
  };

  inactiveLoop = () => {
    if (this.destroying) return;
    const now = new Date().getTime();

    if (now - this.lastFrameTime > 1000) {
      // Если визуализации не было более 1с, то обновление из GameState
      while (this.movesQueue.length) {
        this.movesQueue.pop();
      }
      this._gameState.forceRenew();
    }
    window.setTimeout(this.inactiveLoop, 1000 / 2); // Проверка каждые 0.5с
  };

  destroy = () => {
    this.destroying = true;
    this.removeListeners();
  };

  checkContext = () => {
    this.context = this.canvas.getContext('2d') as CanvasRenderingContext2D;
    if ((document.getElementById('cnvmain') as HTMLCanvasElement).getContext('2d') !== this.context) {
      this.updateContext();
      this.context = (document.getElementById('cnvmain') as HTMLCanvasElement).getContext(
        '2d'
      ) as CanvasRenderingContext2D;
    }
  };

  isAnimating = () => {
    return this.animatedCheckers.length > 0 || this.diceView.rolling;
  };

  layout = () => {
    this.canvas = document.getElementById('cnvmain') as HTMLCanvasElement;
    const canvasDiv = document.getElementById('canvasdiv') as HTMLDivElement;
    this.context = this.canvas.getContext('2d') as CanvasRenderingContext2D;
    const { height, width } = canvasDiv.getBoundingClientRect();
    // Соотношение сторон доски 10 : 13
    if (width / 13 > height / 10) {
      this.canvas.height = height;
      this.canvas.width = (height / 10) * 13;
    } else {
      this.canvas.width = width;
      this.canvas.height = (width / 13) * 10;
    }
    // Добавляем соотношение сторон, чтобы канвас не сплющивался
    this.canvas.style.width = this.canvas.width + 'px';
    this.canvas.style.height = this.canvas.height + 'px';

    this.gameSizes.setCanvasSize(this.canvas.width, this.canvas.height, 1);
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);

    const linePosArray = getPositionsForLines(
      this._gameState.getMyColor(),
      this.gameSizes,
      Boolean(this.myHomeIsOnLeftPref)
    );

    const reversed = this._gameState.getMyColor() !== Color.Black;
    this.lines.forEach((line) => {
      if (line.number === undefined) throw new Error('No number for this line!');
      line.position = linePosArray[line.number];

      if (line.number > 12) {
        line.setOrientation(
          reversed ? LineOrientations.ORIENTATION_NORMAL : LineOrientations.ORIENTATION_REVERSED
        );
      } else {
        line.setOrientation(
          !reversed ? LineOrientations.ORIENTATION_NORMAL : LineOrientations.ORIENTATION_REVERSED
        );
      }

      if (line.number === 26) {
        line.setOrientation(
          !reversed ? LineOrientations.ORIENTATION_NORMAL : LineOrientations.ORIENTATION_REVERSED
        );
      } else if (line.number === 27) {
        line.setOrientation(
          reversed ? LineOrientations.ORIENTATION_NORMAL : LineOrientations.ORIENTATION_REVERSED
        );
      }

      line.size = this.gameSizes.getLineSize();
      if (line.needLayout) line.layout();
    });

    this.boardDrawable.size = this.gameSizes.getCanvasSize();
    this.boardDrawable.position = {
      x: this.gameSizes.getCanvasSize().width / 2,
      y: this.gameSizes.getCanvasSize().height / 2,
    };

    // this.backgroundDrawable.size = this.imageProvider.gameSizes.getCanvasSize();
    // this.backgroundDrawable.position = {
    //   x: this.imageProvider.gameSizes.getCanvasSize().width / 2,
    //   y: this.imageProvider.gameSizes.getCanvasSize().height / 2,
    // };

    if (this.isBoardReversed()) {
      // this.backgroundDrawable.rotation = 180;
      this.boardDrawable.rotation = 180;
    }

    this.doubleCube.size = this.gameSizes.getDoubleCubeSize();
    if (this.isBoardReversed()) {
      this.doubleCube.position = {
        x: this.gameSizes.getDoubleCubeSize().width / 1.1,
        y: this.gameSizes.getCanvasSize().height / 2,
      };
    } else {
      this.doubleCube.position = {
        x: this.gameSizes.getCanvasSize().width - this.gameSizes.getDoubleCubeSize().width / 0.9,
        y: this.gameSizes.getCanvasSize().height / 2,
      };
    }
    this.diceView.recalculateSize(this.canvas);
    this.render();
    this.firstLayoutMade = true;
  };

  updateContext = () => {
    this.canvas = document.getElementById('cnvmain') as HTMLCanvasElement;
    this.context = this.canvas.getContext('2d') as CanvasRenderingContext2D;
    this.requestLayout();
    this.requestRedraw();
  };

  render = () => {
    this.lastRender = new Date().getTime();
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    // this.backgroundDrawable.render(this.context);
    this.renderLines();
    this.boardDrawable.render(this.context);
    this.renderNumbers();
    this.renderCheckers();
    this.isDoubleCube && this.doubleCube.render(this.context);
    if (this.throwFromBottom) {
      this.diceView.render(this.context);
    } else {
      this.context.save();
      this.context.translate(this.canvas.width, this.canvas.height);
      this.context.rotate(Math.PI);
      this.diceView.render(this.context);
      this.context.restore();
    }
    this.renderAnimatedCheckers();

    if (this.checkerInAir) {
      this.checkerInAir.render(this.context);
    }
  };

  renderLines = () => {
    for (let i = 0; i < this.lines.length; i++) {
      this.lines[i].render(this.context);
    }
  };

  renderCheckers = () => {
    for (let i = 0; i < this.lines.length; i++) {
      this.lines[i].renderCheckers(this.context);
    }
  };

  renderNumbers = () => {
    for (let i = 0; i < this.lines.length; i++) {
      if (this.lines[i].getNumber() === 0 || this.lines[i].getNumber() > 24) continue;
      this.lines[i].renderNumber(this.context);
    }
  };

  removeCheckerFromAnimated(checker: ICheckerView) {
    const index = this.animatedCheckers.indexOf(checker);
    if (index !== -1) {
      this.animatedCheckers.splice(index, 1);
    }
  }

  isBoardReversed = () => {
    return this.myHomeIsOnLeftPref && this._gameState.getMyColor() === Color.Black;
  };

  createLines(context: CanvasRenderingContext2D) {
    for (let i = 0; i < this.linesCount; i++) {
      const gameLine = new GameLine(this.imageProvider, this.gameSizes, this.context);
      let currentColor;
      if (i === 0 || i > 24) {
        currentColor = LineColors.COLOR_TRANSPARENT;
      } else {
        currentColor = i % 2 === 0 ? LineColors.COLOR_DARK_GREEN : LineColors.COLOR_LIGHT_GREEN;
      }

      gameLine.setNumber(i, this.context);
      gameLine.setColor(currentColor);
      this.lines.push(gameLine);
    }
  }

  renderAnimatedCheckers = () => {
    const currentTime = new Date().getTime();
    const length = this.animatedCheckers.length;
    const copy = this.animatedCheckers.slice();

    for (let i = length; i >= 0; i--) {
      const animatedChecker = copy[i];
      if (animatedChecker) {
        animatedChecker.render(this.context);
        animatedChecker.performAnimation(currentTime);
      }
    }
  };

  mouseDown = (e: MouseEvent) => {
    if (e.button !== 0)
      //ignore right and middle mouse click
      return;

    const target = e.target || e.currentTarget;
    if (target && target === this.canvas) {
      if (!document.activeElement || document.activeElement.nodeName.toLowerCase() !== 'input') {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    const pos = this.getMousePosRelative(e);
    this.onMouseDown(pos);
  };

  mouseMove = (e: MouseEvent) => {
    const target = e.target || e.currentTarget;
    if (target && target === this.canvas) {
      e.preventDefault();
      e.stopPropagation();
    }

    const pos = this.getMousePosRelative(e);
    this.onMouseMove(pos);
  };

  mouseUp = (e: MouseEvent) => {
    if (e.button !== 0) return;

    const target = e.target || e.currentTarget;
    if (target && target === this.canvas) {
      if (!document.activeElement || document.activeElement.nodeName.toLowerCase() !== 'input') {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    const pos = this.getMousePosRelative(e);
    this.onMouseUp(pos);
  };

  touchDown = (e: TouchEvent) => {
    const target = e.target || e.currentTarget;
    if (target && target === this.canvas) {
      if (!document.activeElement || document.activeElement.nodeName.toLowerCase() !== 'input') {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    if (this.currentTouchId !== -1) {
      return;
    }

    this.currentTouchId = e.changedTouches[0].identifier;

    const pos = this.getTouchPosRelative(e, false);
    this.onMouseDown(pos);
    return false;
  };

  touchMove = (e: TouchEvent) => {
    const target = e.target || e.currentTarget;
    if (target && target === this.canvas) {
      e.preventDefault();
      e.stopPropagation();
    }
    const pos = this.getTouchPosRelative(e, false);
    this.onMouseMove(pos);
    return false;
  };

  touchEnd = (e: TouchEvent) => {
    if (this.currentTouchId !== e.changedTouches[0].identifier) return;

    this.currentTouchId = -1;

    const target = e.target || e.currentTarget;
    if (target && target === this.canvas) {
      e.preventDefault();
      e.stopPropagation();
    }

    const pos = this.getTouchPosRelative(e, true);
    this.onMouseUp(pos);
    return false;
  };

  touchLeave = (e: TouchEvent) => {
    if (this.currentTouchId !== e.changedTouches[0].identifier) return;
    this.currentTouchId = -1;

    const target = e.target || e.currentTarget;
    if (target && target === this.canvas) {
      if (!document.activeElement || document.activeElement.nodeName.toLowerCase() !== 'input') {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    const pos = this.getTouchPosRelative(e, true);
    this.onMouseUp(pos);
    return false;
  };

  touchCancel = (e: TouchEvent) => {
    const target = e.target || e.currentTarget;
    if (target && target === this.canvas) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (this.currentTouchId !== e.changedTouches[0].identifier) return;

    this.currentTouchId = -1;

    const pos = this.getTouchPosRelative(e, true);
    this.onMouseUp(pos);
    return false;
  };

  isClickOnDoubleCube = (pos: Coordinates) => {
    if (
      pos.x > this.doubleCube.position.x - this.doubleCube.size.width / 2 &&
      pos.x < this.doubleCube.position.x + this.doubleCube.size.width / 2 &&
      pos.y > this.doubleCube.position.y - this.doubleCube.size.height / 2 &&
      pos.y < this.doubleCube.position.y + this.doubleCube.size.height / 2
    ) {
      return true;
    }
    return false;
  };

  onMouseDown = (pos: Coordinates) => {
    this.mouseDownPos = pos;

    if (this._gameState.imWatching() || !this._gameState.isMyTurn()) {
      return;
    }
    this._gameState.checkInvariant();

    if (this.isClickOnDoubleCube(pos)) {
      this._gameState.doubleCubeClick();
      return;
    }

    this.lastClickTime = new Date().getTime();

    this.setStatusMessageInteractable(false);
    const line = this.getLineFromPos(pos);
    if (!line) {
      console.log('you don`t click on line');
      return;
    }

    if (this.lineIsAnimated(line)) {
      return;
    }

    if (this._gameState.getPhase() === BackgammonPhases.ACTIVATE) {
      return;
    }

    const myColor = this._gameState.getMyColor();
    this.availableLineNumbers = this._gameState.getAvailableMoves(
      line.getNumber(),
      myColor,
      true,
      this._gameState.getDice().getAvailableMoves(),
      true
    );

    if (!this.availableLineNumbers || !this.availableLineNumbers.length) return;
    if (line.getCheckersColor() !== myColor) return;

    this.higlightAvailableMoves(line.getNumber(), this.availableLineNumbers);

    const pressedChecker = line.getTopChecker();

    if (!pressedChecker) return;

    this.sourceLine = line;
    this.checkerInAir = pressedChecker;
    this.requestRedraw();
  };

  onMouseDoubleClick = (pos: Coordinates) => {
    // TODO Logger
    // Logger.addEventToLog("mouseMoves", { type: "mouseClick", position: pos});
    // PerformanceLock.instance.lockForEvent(PerformanceLock.inputEvent);

    if (!this.singleClickPref) {
      return;
    }

    const line = this.getLineFromPos(pos);
    if (!line) {
      console.log('you don`t click on line');
      return;
    }
    this.sourceLine = line;

    if (line.getCheckersColor() !== this._gameState.getMyColor()) {
      return;
    }
    // PerformanceLock.instance.lockForEvent(PerformanceLock.calculationEvent);
    this._gameState.moveFromViewByClick(line.getNumber());
  };

  onMouseUp = (pos: Coordinates) => {
    // TODO Logger
    // Logger.addEventToLog("mouseMoves", { type: "mouseUp", position: pos});
    this.setStatusMessageInteractable(true);
    if (!this.sourceLine) {
      return;
    }

    this.setLinesRegular();
    const line = this.getLineFromPos(pos);
    const isClick = this.lastClickTime && new Date().getTime() - this.lastClickTime < this.doubleClickDelayMs;
    let sameLineAsSource;
    let availableToPlaceLine;
    if (line && this.availableLineNumbers && this.availableLineNumbers.length) {
      sameLineAsSource = line.getNumber() === this.sourceLine.getNumber();
      availableToPlaceLine = this.availableLineNumbers.indexOf(line.getNumber()) !== -1;
    } else {
      sameLineAsSource = false;
      availableToPlaceLine = false;
    }

    if (this.checkerInAir) {
      if (
        line && //not on line
        (availableToPlaceLine || (!availableToPlaceLine && isClick)) &&
        (!sameLineAsSource || (sameLineAsSource && isClick))
      ) {
        // same line as get checker
        if (isClick) {
          this.returnCheckerBackInstantly();
          this.onMouseDoubleClick(this.mouseDownPos);
          return;
        }

        this.insertLine = line;
        const move = new Move();
        move.setSourceLineNumber(this.sourceLine.getNumber());
        move.setInsertLineNumber(this.insertLine.getNumber());
        move.color = this.checkerInAir.getColor();

        this._gameState.moveFromView(move);
      } else {
        //return back
        const vector = { x: 0, y: 0 };
        vector.x = Math.abs(this.mouseDownPos.x - pos.x);
        vector.y = Math.abs(this.mouseDownPos.y - pos.y);
        if (this.getLength(vector) < this.gameSizes.getCheckerSize().width) {
          this.returnCheckerBackInstantly();
        } else {
          this.returnCheckerBack();
        }
      }
    }
    this.requestRedraw();
  };

  returnCheckerBack = () => {
    const moveChecker = this.checkerInAir;
    this.checkerInAir = undefined;
    if (!this.sourceLine) return;
    if (!moveChecker) throw new Error('No checker to return');
    const fromCords = { x: moveChecker.position.x, y: moveChecker.position.y };
    const cordsTo = this.sourceLine.getPointForNewChecker();
    const currentTime = new Date().getTime();
    moveChecker.setAnimating(false);
    this.animatedCheckers.push(moveChecker);

    moveChecker.createAnimation(
      fromCords,
      cordsTo,
      this.BACK_ANIMATION_SPEED,
      currentTime,
      this.sourceLine,
      (checkerView: ICheckerView, toLine: GameLine) => {
        checkerView.setAnimating(false);
        this.removeCheckerFromAnimated(checkerView);
        toLine.addChecker(checkerView);
        this.requestRedraw();
        this.requestLayout();
        // this.needsUpdate = true;
        checkerView.animation = undefined;
        this.checkBoard();
      }
    );
    moveChecker.setAnimating(true);
    this.requestRedraw();
  };

  returnCheckerBackInstantly = () => {
    const moveChecker = this.checkerInAir;
    if (!moveChecker) throw new Error('No checker to return back');
    this.removeCheckerFromAnimated(moveChecker);
    this.checkerInAir = undefined;
    if (!this.sourceLine) return;
    this.sourceLine.addChecker(moveChecker);
    this.requestRedraw();
    this.requestLayout();
  };

  onMouseMove = (pos: Coordinates) => {
    if (this.checkerInAir) {
      // PerformanceLock.instance.lockForEvent(PerformanceLock.inputEvent);
      this.checkerInAir.position = pos;
      this.requestRedraw();
    }
  };

  onMouseLeave = (e: MouseEvent) => {};

  setStatusMessageInteractable(interactable: boolean) {
    if (interactable) {
      // $('#StatusMessage').css('pointer-events', 'auto');
    } else {
      // $('#StatusMessage').css('pointer-events', 'none');
    }
  }

  higlightAvailableMoves(
    currentLineNumber: number,
    availableLinesNumbers: (number | undefined)[] | undefined
  ) {
    const fromBar = currentLineNumber === 27 || currentLineNumber === 26;
    const myColor = this._gameState.getMyColor();
    if (availableLinesNumbers) {
      for (let i = 0; i < availableLinesNumbers.length; i++) {
        const availableNumbers = availableLinesNumbers[i];
        if (availableNumbers) {
          if (
            (availableNumbers > currentLineNumber && myColor === Color.Black) ||
            (availableNumbers < currentLineNumber && myColor === Color.White)
          ) {
            this.getLine(availableNumbers)?.setState(LineStates.STATE_AVAILABLE);
          } else {
            this.getLine(availableNumbers)?.setState(
              fromBar ? LineStates.STATE_AVAILABLE : LineStates.STATE_UNDO
            );
          }
        }
      }
    }
  }

  setLinesRegular = () => {
    for (let i = 0; i < this.lines.length; i++) {
      this.lines[i].setState(LineStates.STATE_NORMAL);
    }
  };

  getMousePosRelative(event: MouseEvent) {
    const rect = this.canvas.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;
    return {
      x: mouseX,
      y: mouseY,
    };
  }

  getTouchPosRelative(event: TouchEvent, isTouchEnd: boolean) {
    // if ( !event ) event = window.event;
    // let touches;
    // if (isTouchEnd) {
    // touches = event.changedTouches;
    // } else {
    // touches = event.touches;
    // }
    // let mouseX = (this.clipVal( Math.round( touches[0].pageX - $("#cnvmain").offset().left ), 0, this.width ))* this.ratio;
    // let mouseY = (this.clipVal( Math.round( touches[0].pageY - $("#cnvmain").offset().top ), 0, this.height ))* this.ratio;

    return {
      x: event.changedTouches[0].pageX,
      y: event.changedTouches[0].pageY,
    };
  }

  getLineFromPos(pos: Coordinates) {
    let result = null;
    for (let i = 0; i < this.lines.length; i++) {
      const line = this.lines[i];
      if (
        pos.x > line.position.x - line.size.width / 2 &&
        pos.x < line.position.x + line.size.width / 2 &&
        pos.y > line.position.y - (line.size.height / 2) * 1.3 &&
        pos.y < line.position.y + (line.size.height / 2) * 1.3
      ) {
        result = line;
        break;
      }
    }
    return result;
  }

  clipVal(val: number, min: number, max: number) {
    if (val < min) {
      return min;
    } else if (val > max) {
      return max;
    } else {
      return val;
    }
  }

  getLength(vector: Coordinates) {
    return Math.sqrt(Math.pow(vector.x, 2) + Math.pow(vector.y, 2));
  }

  checkBoard = () => {
    if (this.isAnimating()) {
      return;
    }
    let sum = 0;
    for (let i = 0; i < 28; i++) {
      const line = this.getLine(i);
      if (!line) continue;
      sum += line.getCheckersCount();
    }
    if (sum !== 30) {
      while (this.movesQueue.length) {
        this.movesQueue.pop();
      }
      while (this.animatedCheckers.length) {
        this.animatedCheckers.pop();
      }
      this.checkerInAir = undefined;
      this._gameState.forceRenew();
    }
  };

  lineIsAnimated(gameLine: GameLine) {
    for (let i = 0; i < this.animatedCheckers.length; i++) {
      const checker = this.animatedCheckers[i];
      if (checker.animation && checker.animation.getDestinationLine().getNumber() === gameLine.getNumber()) {
        return true;
      }
    }
    return false;
  }

  get gameState() {
    return this._gameState;
  }
}
